import {type KeyboardEvent, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {Box, Chip, Collapse, Typography} from '@mui/material';

import {WorkforceProfileAnalysisDataInputSectionContent} from './WorkforceProfileAnalysisDataInputSectionContent';
import {
  analysisInputExpandIconSx,
  analysisInputMetaLabelSx,
  analysisInputPaperSx,
  getAnalysisInputHeaderSx,
} from './workforceProfileAnalysisDataInputSectionSx';
import {EmpionPaper} from '../../../../../components/EmpionPaper/EmpionPaper';
import {WorkforceProfileAnalysisDataInput} from '../../types/WorkforceProfileTypes';
import {confidenceChipSx} from '../../utils/WorkforceProfileDetail/workforceAnalysisInputConfidenceUtils';
import {getAnalysisInputItemCount} from '../../utils/WorkforceProfileDetail/workforceAnalysisInputUtils';

export interface Props {
  data: WorkforceProfileAnalysisDataInput;
  alwaysExpanded?: boolean;
}

export const WorkforceProfileAnalysisDataInputSection = ({data, alwaysExpanded}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'workforceProfilesPage.detail.analysisInput'});
  const [expanded, setExpanded] = useState(!!alwaysExpanded);

  const confidenceLabel = (value: string) => t(`confidence.${value.toLowerCase()}`, {defaultValue: value});
  const itemCount = useMemo(() => getAnalysisInputItemCount(data), [data]);
  const isOpen = alwaysExpanded || expanded;

  const toggleExpanded = () => {
    if (!alwaysExpanded) setExpanded((previous) => !previous);
  };

  const handleHeaderKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleExpanded();
    }
  };

  return (
    <EmpionPaper sx={analysisInputPaperSx}>
      <Box
        onClick={alwaysExpanded ? undefined : toggleExpanded}
        onKeyDown={alwaysExpanded ? undefined : handleHeaderKeyDown}
        role={alwaysExpanded ? undefined : 'button'}
        tabIndex={alwaysExpanded ? undefined : 0}
        aria-expanded={alwaysExpanded ? undefined : expanded}
        sx={getAnalysisInputHeaderSx(isOpen, !!alwaysExpanded)}
      >
        <Box sx={{flex: 1, minWidth: 0}}>
          <Typography variant='subtitle2' sx={{fontWeight: 600}}>
            {t('title')}
          </Typography>
          {!isOpen && (
            <Typography variant='caption' color='text.secondary' sx={{display: 'block', mt: 0.5, lineHeight: 1.45}}>
              {t('collapsedHint')}
            </Typography>
          )}
        </Box>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0}}>
          {!isOpen && itemCount > 0 && (
            <Typography variant='caption' sx={analysisInputMetaLabelSx}>
              {t('itemCount', {count: itemCount})}
            </Typography>
          )}
          {data.extractionConfidence && (
            <Chip
              label={t('extractionConfidence', {level: confidenceLabel(data.extractionConfidence)})}
              size='small'
              sx={{height: 24, fontWeight: 600, ...confidenceChipSx(data.extractionConfidence)}}
            />
          )}
          {!alwaysExpanded && <ExpandMoreIcon sx={analysisInputExpandIconSx(expanded)} />}
        </Box>
      </Box>

      {alwaysExpanded ? (
        <WorkforceProfileAnalysisDataInputSectionContent data={data} />
      ) : (
        <Collapse in={expanded} timeout='auto' unmountOnExit>
          <WorkforceProfileAnalysisDataInputSectionContent data={data} />
        </Collapse>
      )}
    </EmpionPaper>
  );
};
